import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FleetWebApi from "../Api/FleetWebApi";
import FlowEventsTab from "./FlowEventsTab";
import DebugWaveformsTab from "./DebugWaveformsTab";
import { formatMinute, fromUtcInput, toUtcInput } from "./harpieUtils";

// The window the query on opening covers, ending now.
const DEFAULT_HOURS = 24;
// Stable, so the tabs keep their selection until a query returns.
const NOTHING = [];

// AL/Dbg/Flow events and ultrasonic debug waveforms from FleetData, ported
// from HarpieView in fleettools. The query is kept in the URL, so a view can
// be linked: /harpie?gateway=…&sensor=…&start=2026-10-21T20:30&end=…
function HarpieView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [gateway, setGateway] = useState(searchParams.get("gateway") || "");
  const [sensor, setSensor] = useState(searchParams.get("sensor") || "");
  const [start, setStart] = useState(
    searchParams.get("start") || toUtcInput(new Date(Date.now() - DEFAULT_HOURS * 3600 * 1000))
  );
  // Blank is now.
  const [end, setEnd] = useState(searchParams.get("end") || "");
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const query = async () => {
    const startDate = fromUtcInput(start);
    const endDate = end ? fromUtcInput(end) : new Date();
    if (!startDate) {
      setError("Start is needed");
      return;
    }
    if (!endDate) {
      setError("End must be a date and time, or blank for now");
      return;
    }
    if (startDate >= endDate) {
      setError(`Start (${formatMinute(startDate)}) must be before End (${formatMinute(endDate)})`);
      return;
    }
    setError(null);
    setLoading(true);
    setStatus(`Querying ${formatMinute(startDate)} – ${formatMinute(endDate)} UTC…`);
    const params = { start };
    if (gateway.trim()) params.gateway = gateway.trim();
    if (sensor.trim()) params.sensor = sensor.trim();
    if (end) params.end = end;
    setSearchParams(params, { replace: true });
    try {
      const response = await FleetWebApi.getHarpieData(
        gateway.trim() || null,
        sensor.trim() || null,
        startDate.toISOString(),
        endDate.toISOString()
      );
      setResult(response);
      const sensors = new Set(response.flowRecords.map((r) => r.sensorId)).size;
      const cores = new Set(response.waveforms.map((w) => w.coreId)).size;
      // Flow events are fetched oldest first and waveforms newest first, so a
      // limit cuts off opposite ends of the window.
      const capped = [
        response.flowLimitReached ? "flow events (showing the oldest)" : null,
        response.waveformLimitReached ? "waveforms (showing the newest)" : null,
      ].filter(Boolean);
      let text =
        `${formatMinute(startDate)} – ${formatMinute(endDate)} UTC: ` +
        `${response.flowRecords.length} flow records from ${sensors} sensors, ` +
        `${response.waveforms.length} waveforms from ${cores} coreIDs.`;
      if (capped.length) {
        text += ` Limit reached for ${capped.join(" and ")}; narrow the window.`;
      }
      setStatus(text);
    } catch (e) {
      setStatus("");
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Fills both tabs with the default window, or the linked one.
  useEffect(() => {
    query();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="container">
      <h1>Harpie</h1>
      <Paper sx={{ p: 2 }}>
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            query();
          }}
          sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}
        >
          <TextField
            label="Gateway coreID"
            placeholder="any"
            size="small"
            value={gateway}
            onChange={(e) => setGateway(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 250 }}
          />
          <TextField
            label="Sensor ID"
            placeholder="any"
            size="small"
            value={sensor}
            onChange={(e) => setSensor(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 250 }}
          />
          <TextField
            label="Start (UTC)"
            type="datetime-local"
            size="small"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End (UTC)"
            type="datetime-local"
            size="small"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText={end ? null : "blank is now"}
          />
          <Button type="submit" variant="contained" startIcon={<SearchIcon />} disabled={loading}>
            Query
          </Button>
          {loading ? <CircularProgress size={18} /> : null}
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 300 }}>
            {status}
          </Typography>
        </Box>
        {error ? (
          <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}
        <Tabs value={tab} onChange={(e, value) => setTab(value)} sx={{ mt: 1 }}>
          <Tab label="Flow Events" />
          <Tab label="Debug Waveforms" />
        </Tabs>
        <Box sx={{ pt: 2, display: tab === 0 ? "block" : "none" }}>
          <FlowEventsTab records={result?.flowRecords || NOTHING} loaded={!!result} />
        </Box>
        <Box sx={{ pt: 2, display: tab === 1 ? "block" : "none" }}>
          <DebugWaveformsTab waveforms={result?.waveforms || NOTHING} loaded={!!result} />
        </Box>
      </Paper>
    </div>
  );
}

export default HarpieView;
